import { NoSsr } from "@mui/base";
import { useAtomValue } from "jotai";
import type { NextPageContext, PageProps as NextPageProps } from "next";
import { useEffect, useState } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { isString } from "scmp-sdk";

import { notEmpty, parseQueryString } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PlusMobileScreen } from "scmp-app/components/plus/mobile-screen";
import { PlusHomeSeo } from "scmp-app/components/plus/plus-home-seo";
import { SearchContextProvider } from "scmp-app/components/search/contexts";
import { sanitizeKeyword } from "scmp-app/components/search/helpers";
import { SearchResultPanel } from "scmp-app/components/search/search-result-panel";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { useGetPageLocationForPlusPage } from "scmp-app/pages/plus/hooks";
import { plusGlobalCss } from "scmp-app/pages/plus/styles";
import type { searchPlusSearchPageQuery } from "scmp-app/queries/__generated__/searchPlusSearchPageQuery.graphql";

import { SearchContainer, SearchWrapper, StyledSearchInput } from "./styles";

const query = graphql`
  query searchPlusSearchPageQuery($nodeId: ID!, $hasNodeId: Boolean!) {
    node(id: $nodeId) @include(if: $hasNodeId) {
      ...plusHomeSeoNode
    }
  }
`;

type PageProps = NextPageProps & { q: string };

const SearchPage: RelayComponent<PageProps, searchPlusSearchPageQuery> = ({
  preloadedQuery,
  q: routeQueryValue,
}) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);
  const cleanRouteQueryValue = sanitizeKeyword(routeQueryValue);
  const [currentValue, setCurrentValue] = useState(cleanRouteQueryValue);
  const pageLocation = useGetPageLocationForPlusPage();
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    setCurrentValue(cleanRouteQueryValue);
  }, [cleanRouteQueryValue]);

  useEffect(() => {
    if (!notEmpty(pageLocation)) {
      return;
    }
    trackPageView?.({
      page_location: pageLocation,
    });
  }, [pageLocation, trackPageView]);

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  return (
    <SearchContextProvider currentValue={currentValue} searchPageType="plus">
      <PlusHomeSeo reference={queryReference.node} title="Search" />
      <PlusMobileScreen />
      <SearchContainer>
        <SearchWrapper>
          <StyledSearchInput $stickyStackedHeight={appBarStickyHeight} />
          <NoSsr>
            <SearchResultPanel />
          </NoSsr>
        </SearchWrapper>
      </SearchContainer>
    </SearchContextProvider>
  );
};

const getPageProps = (context: NextPageContext): PageProps => ({
  animateKey: "plus-search",
  appBarConfiguration: {
    variant: "scmp/plus",
  },
  appInitializerConfiguration: {
    application: "plus",
  },
  extraGlobalCss: plusGlobalCss,
  q: parseQueryString(context.query.q) ?? "",
});

export default withRelay(SearchPage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext(context) {
    return {
      hasNodeId: notEmpty(context.query?.nodeId),
      nodeId: isString(context.query.nodeId) ? (context.query?.nodeId ?? "") : "",
    };
  },
});
